import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Input, Table } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { InputRef } from 'antd/es/input';
import { Clinic } from '../../../models/MedVox/Clinic';
import styles from './styles.module.css';

interface Props {
  selectedClinics: Clinic[];
  allClinics: Clinic[];
  onChange: (clinics: Clinic[]) => void;
  disabled?: boolean;
  className?: string;
}

export const ClinicSelectionEditor: FC<Props> = memo(
  ({ selectedClinics, allClinics, onChange, disabled = false, className }) => {
    const [searchValue, setSearchValue] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<InputRef>(null);

    useEffect(() => {
      if (!dropdownOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    const filteredClinics = useMemo(() => {
      const available = allClinics.filter(
        (clinic) =>
          !selectedClinics.some((selected) => selected.id === clinic.id),
      );
      if (!searchValue.trim()) return available;
      const query = searchValue.toLowerCase();
      return available.filter((clinic) =>
        clinic.name.toLowerCase().includes(query),
      );
    }, [allClinics, selectedClinics, searchValue]);

    const handleClinicClick = useCallback(
      (clinicId: string) => {
        if (disabled) return;
        const clinicToAdd = allClinics.find((clinic) => clinic.id === clinicId);
        if (!clinicToAdd) return;
        onChange([...selectedClinics, clinicToAdd]);
        setSearchValue('');
        setDropdownOpen(false);
        searchInputRef.current?.blur();
      },
      [allClinics, disabled, onChange, selectedClinics],
    );

    const handleDelete = useCallback(
      (clinicId: string) => {
        if (disabled) return;
        onChange(selectedClinics.filter((clinic) => clinic.id !== clinicId));
      },
      [disabled, onChange, selectedClinics],
    );

    const columns: ColumnsType<Clinic> = useMemo(
      () => [
        {
          title: 'Наименование',
          dataIndex: 'name',
          key: 'name',
        },
        {
          title: '',
          key: 'action',
          width: 50,
          render: (_, record) => (
            <DeleteOutlined
              className={styles.deleteBtn}
              onClick={() => handleDelete(record.id)}
            />
          ),
        },
      ],
      [handleDelete],
    );

    const renderDropdown = () => {
      if (!dropdownOpen || disabled) return null;

      if (filteredClinics.length === 0) {
        return (
          <div className={styles.dropdown}>
            <div className={styles.dropdownEmpty}>
              Нет доступных медицинских организаций
            </div>
          </div>
        );
      }

      return (
        <ul className={styles.dropdown} role="listbox">
          {filteredClinics.map((clinic) => (
            <li
              key={clinic.id}
              className={styles.dropdownItem}
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                handleClinicClick(clinic.id);
              }}
            >
              {clinic.name}
            </li>
          ))}
        </ul>
      );
    };

    return (
      <div className={className}>
        <div ref={containerRef} className={styles.searchContainer}>
          <Input
            ref={searchInputRef}
            placeholder="Поиск по медицинской организации"
            prefix={<SearchOutlined />}
            allowClear
            value={searchValue}
            disabled={disabled}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
          />
          {renderDropdown()}
        </div>

        <div
          className={styles.selectedTable}
          data-testid="selected-clinics-list"
        >
          <Table
            columns={columns}
            dataSource={selectedClinics}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{
              emptyText: (
                <div className={styles.emptyText}>
                  Нет выбранных медицинских организаций
                </div>
              ),
            }}
          />
        </div>
      </div>
    );
  },
);

ClinicSelectionEditor.displayName = 'ClinicSelectionEditor';
